import type { ClinicalDocumentData } from "@qendo/profile/files";
import { useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { FileGalleryListingItem } from "~/components/layout/gallery/FileGalleryListingItem";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { Page } from "~/components/layout/Page";
import { Spacer } from "~/components/layout/Spacer";
import { Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { Gap, Items, Padding } from "~/constants/layout";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { filled } from "~/util";
import { formatDateForHumans } from "~/util/dates";
import { plural } from "~/util/string";

export type ClinicalDocumentProps = {
    item: ClinicalDocumentData;
};

export const ClinicalDocument = ({ item }: ClinicalDocumentProps) => {
    const { destroyDocument } = useGallery();
    const { can } = useProfile();
    const { loading, setLoading, addToast } = useFeedback();
    const [confirm, setConfirm] = useState(false);

    const can_delete = can("update", "documents") && !loading;

    const doDelete = () => {
        if (!can_delete) {
            return;
        }

        setLoading(true);

        destroyDocument(item.id)
            .then(() => {
                addToast({ content: "Clinical document deleted." });
            })
            .finally(() => setLoading(false));
    };

    return (
        <Page spacer>
            <View style={[Gap.sm, Items.start]}>
                <Only when={item.pain_plan}>
                    <Badge title="Prescribed pain plan" variant="info" size="sm" />
                </Only>
                <Title size="xl">{item.name}</Title>
            </View>
            <DataList>
                <DataListItem title="Date on documentation" data={formatDateForHumans(item.date, "medium")} />
                {filled(item.description) && <DataListItem title="Description" data={item.description} />}
            </DataList>
            {filled(item.files) && (
                <ListingGroup title={`Attached ${plural("file", item.files)}`}>
                    {item.files.map((file, idx, array) => (
                        <FileGalleryListingItem
                            key={file.id}
                            item={file}
                            document={item.id}
                            last={idx === array.length - 1}
                        />
                    ))}
                </ListingGroup>
            )}
            <Can ability="update" section="documents">
                <Spacer />
                <View style={[Items.center, Padding.bottom_md]}>
                    <Button
                        icon="trash"
                        title="Delete document"
                        variant="destructive"
                        size="lg"
                        onPress={() => setConfirm(true)}
                    />
                </View>
                {confirm && (
                    <ConfirmationModal
                        onConfirm={doDelete}
                        title="Are you sure you want to delete this document?"
                        confirmText="Delete document"
                        confirmVariant="destructive-quiet"
                        loading={loading}
                        onRequestClose={() => setConfirm(false)}
                    />
                )}
            </Can>
        </Page>
    );
};
